import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material'
import { X, Link2, Sparkles, Check } from 'lucide-react'
import { Platform } from 'graphql/generated/graphql'
import AnimatedLoadingText from 'components/AnimatedLoadingText/AnimatedLoadingText'
import { LOCAL, DEVELOP } from 'constants/env'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

const PLATFORM_OPTIONS: Array<{ value: Platform; label: string; emoji: string }> = [
  { value: Platform.Instagram, label: 'Instagram', emoji: '📷' },
  { value: Platform.TikTok, label: 'TikTok', emoji: '🎵' },
  { value: Platform.Youtube, label: 'YouTube', emoji: '🎥' },
  { value: Platform.Reddit, label: 'Reddit', emoji: '🔴' },
  { value: Platform.Twitter, label: 'Twitter', emoji: '🐦' },
]

interface ClipToSignalDialogProps {
  open: boolean
  onClose: () => void
  onCreated: () => void
}

export default function ClipToSignalDialog({ open, onClose, onCreated }: ClipToSignalDialogProps): JSX.Element {
  const [url, setUrl] = useState('')
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([Platform.Instagram, Platform.TikTok])
  const [creating, setCreating] = useState(false)
  const [result, setResult] = useState<{ videoTitle: string; talkingPoints: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleClose = (): void => {
    setUrl('')
    setResult(null)
    setError(null)
    setCreating(false)
    onClose()
  }

  const handleCreate = async (): Promise<void> => {
    if (!url.trim()) return
    setCreating(true)
    setError(null)
    try {
      const response = await fetch(`${config.API_URL}/api/podcast/clip-to-signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: url.trim(), platforms: selectedPlatforms }),
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        throw new Error(data.error ?? 'Failed to create signal')
      }
      setResult({ videoTitle: data.data.videoTitle, talkingPoints: data.data.talkingPoints })
      setTimeout(() => {
        handleClose()
        onCreated()
      }, 2000)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const togglePlatform = (p: Platform): void => {
    setSelectedPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={window.innerWidth < 600}
      PaperProps={{
        sx: {
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(24px)',
          border: { xs: 'none', sm: '1px solid rgba(168, 85, 247, 0.2)' },
          borderRadius: { xs: 0, sm: '16px' },
          color: 'white',
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link2 size={20} color="#a855f7" />
          <Typography sx={{ fontWeight: 600, fontSize: { xs: '16px', sm: '18px' } }}>
            Create Signal from Clip
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!result ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <Box>
              <Typography sx={{ mb: 1, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                Paste a YouTube or Spotify link
              </Typography>
              <Typography sx={{ mb: 1.5, fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                We&apos;ll extract the key topics and find social conversations about them.
              </Typography>
              <TextField
                fullWidth
                placeholder="YouTube or Spotify episode URL..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { handleCreate().catch(console.error) } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(168,85,247,0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ mb: 1, fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                Find conversations on
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {PLATFORM_OPTIONS.map((p) => (
                  <Chip
                    key={p.value}
                    label={`${p.emoji} ${p.label}`}
                    onClick={() => togglePlatform(p.value)}
                    size="small"
                    sx={{
                      backgroundColor: selectedPlatforms.includes(p.value) ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.05)',
                      border: selectedPlatforms.includes(p.value) ? '1px solid rgba(168,85,247,0.5)' : '1px solid rgba(255,255,255,0.1)',
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(168,85,247,0.2)' },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {error && (
              <Typography sx={{ color: '#ef4444', fontSize: '13px' }}>{error}</Typography>
            )}

            <Button
              onClick={() => { handleCreate().catch(console.error) }}
              disabled={!url.trim() || creating || selectedPlatforms.length === 0}
              startIcon={creating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Sparkles size={16} />}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                color: 'white',
                borderRadius: '12px',
                textTransform: 'none',
                py: 1.2,
                fontWeight: 600,
              }}
            >
              {creating ? <AnimatedLoadingText messages="Analyzing clip" fontSize="14px" color="white" /> : 'Create Signal'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Check size={24} color="#22c55e" />
            </Box>
            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '16px', mb: 0.5 }}>
              Signal created from clip
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', mb: 1.5 }}>
              {result.videoTitle}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', lineHeight: 1.5 }}>
              {result.talkingPoints}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
